import { format as dateFormatter, parse } from 'date-fns';
import { Data, PbDate, PbDateSchema, Timestamp, TimestampSchema, isMessage } from '@/connect';

export type SupportedDates = Date | string | PbDate | Timestamp | number;

export function ifTimestampToDate<T>(value: T): T extends Data<Timestamp> ? Date : T {
  return (
    isMessage(value, TimestampSchema)
      ? new Date(Number(value.seconds * 1000n) + value.nanos / 1e9)
      : value
  ) as T extends Data<Timestamp> ? Date : T;
}

/**
 * Formats a date object to a string
 * This function takes a variety of params and tries its best ot format it
 */
export function formatAnyDate(
  date: SupportedDates | undefined | null,
  format: string,
  options?: Parameters<typeof dateFormatter>[2]
): string {
  try {
    if (!date) return '–';

    if (isMessage(date, TimestampSchema)) {
      return dateFormatter(ifTimestampToDate(date), format, options);
    } else if (typeof date === 'number' || date instanceof Date) {
      return dateFormatter(date, format, options);
    } else if (typeof date === 'string') {
      // attempt to turn into a date object
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.valueOf())) throw new Error('invalid date');

      return dateFormatter(parsedDate, format, options);
    } else if (isMessage(date, PbDateSchema)) {
      // attempt to turn PbDate into a Date object
      // NOTE: In Date, the monthIndex is a number between 0 and 11 (January to December)
      //       In PbDate, the month is a number between 1 and 12
      const parsedDate = new Date(date.year, date.month - 1, date.day);
      if (isNaN(parsedDate.valueOf())) throw new Error('invalid date');

      return dateFormatter(parsedDate, format, options);
    } else {
      return '–';
    }
  } catch {
    return `${date}`;
  }
}

export function parseDate(date: string, format: string = 'yyyy-MM-dd', timezone?: string): Date {
  timezone ??= Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date(new Date().toLocaleString('en', { timeZone: timezone }));
  return parse(date, format, now);
}

export function utcToLocal(date: Date): Date {
  return new Date(+date - new Date().getTimezoneOffset() * 60000);
}
